import React, { Fragment, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';
import elevation from '@/theme/elevation';

// components
import { DateIcon } from '@/components/atoms/DateIcon/DateIcon';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';

// store
import { Webinar } from '@karehero/models';

// utils
import { buildImageUrl } from '@/utils/sanity';

/**
 * WebinarCard is a container for the content of a Card.
 */
export const WebinarCard = ({
  webinar,
  onClick,
  isLoading,
}: WebinarCardProps) => {
  // memos
  const headerImageUrl = useMemo(
    () => buildImageUrl(webinar?.headerImageUrl, 300),
    [webinar],
  );

  return (
    <ContentItem onClick={() => webinar && onClick?.(webinar)}>
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <ContentHeader>
            <Skeleton shape='header' isSquare />
          </ContentHeader>
        }
        content={
          <ContentHeader>
            <img
              src={headerImageUrl}
              alt='header'
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src =
                  'https://cdn.sanity.io/images/x7r1t5o8/production/e8579e2a4c3766cf0cf99a24a9d0679d2d2bbeb4-5322x2365.jpg';
              }}
            />
            <IconWrapper>
              <DateIcon date={new Date(webinar?.date || '')} size='sm' />
            </IconWrapper>
          </ContentHeader>
        }
      />
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <ContentBody>
            <Skeleton shape='subtitle' />
            <Skeleton shape='paragraph' paragraphLength={3} />
          </ContentBody>
        }
        content={
          <ContentBody>
            <ContentBodyHeader>
              <ContentTags>
                {webinar?.tags?.map((tag, i) => (
                  <Fragment key={i}>
                    <ContentTag key={`${webinar.title}-${tag.label}`}>
                      {tag.label}
                    </ContentTag>
                    {webinar?.tags && i !== webinar?.tags?.length - 1 && (
                      <div>-</div>
                    )}
                  </Fragment>
                ))}
              </ContentTags>
              {/* <Bookmark size={16} /> */}
            </ContentBodyHeader>
            <ContentTitle>{webinar?.title}</ContentTitle>
            <ContentDescription>{webinar?.description}</ContentDescription>
          </ContentBody>
        }
      />
    </ContentItem>
  );
};

const ContentItem = ({
  children,
  onClick,
}: {
  children?: React.ReactNode;
  onClick?: () => void;
}) => (
  <Stack
    onClick={onClick}
    sx={{
      position: 'relative',
      borderRadius: sp[8],
      minWidth: '250px',
      height: '300px',
      maxHeight: '300px',
      boxSizing: 'border-box',
      background: '#ffffff',
      boxShadow: elevation[1],
      overflow: 'hidden',
      cursor: 'pointer',
      userSelect: 'none',
      border: '1px solid #cbc5b9',
      transitionDuration: '0.2s',
      '&:hover': { transform: 'scale(1.05)' },
    }}
  >
    {children}
  </Stack>
);
const ContentHeader = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: '150px',
      height: '150px',
      maxHeight: '150px',
      display: 'flex',
      '& > img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        userSelect: 'none',
        WebkitUserDrag: 'none',
      },
    }}
  >
    {children}
  </Box>
);
const IconWrapper = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      position: 'absolute',
      top: '128px',
      left: '14px',
      width: '80px',
      height: '80px',
    }}
  >
    {children}
  </Box>
);
const ContentBody = ({ children }: { children?: React.ReactNode }) => (
  <Stack sx={{ p: `${sp[16]} ${sp[12]}`, gap: sp[4], maxHeight: '150px' }}>
    {children}
  </Stack>
);
const ContentBodyHeader = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    {children}
  </Box>
);
const ContentTags = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: sp[8],
      typography: 'bodyXs',
      color: '#8c8c8d',
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);
const ContentTag = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      typography: 'bodyXs',
      color: '#8c8c8d',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {children}
  </Box>
);
const ContentTitle = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ typography: 'bodySmSemibold', color: '#000000' }}>{children}</Box>
);
const ContentDescription = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      display: '-webkit-box',
      typography: 'bodySm',
      color: '#404042',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    }}
  >
    {children}
  </Box>
);

interface WebinarCardProps {
  webinar?: Partial<Webinar>;
  onClick?: (webinar: Partial<Webinar>) => void;
  isLoading?: boolean;
}

WebinarCard.displayName = 'WebinarCard';

export default WebinarCard;
